/**
 * QMS Create CAPA Modal
 * Modal for creating a CAPA from an existing ticket
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, Plus } from 'lucide-react';
import { useCreateCapaFromTicket } from '../../hooks/useQmsTicket';
import type {
  QmsTicket,
  QmsCreateCapaPayload,
  QmsDepartment,
  QmsPriority,
} from '../../types/qms.types';

interface QmsCreateCapaModalProps {
  ticket: QmsTicket;
  isOpen: boolean;
  onClose: () => void;
  onCapaCreated: (capa: QmsTicket) => void;
}

export function QmsCreateCapaModal({
  ticket,
  isOpen,
  onClose,
  onCapaCreated,
}: QmsCreateCapaModalProps) {
  const { t } = useTranslation('qms');
  const createCapaMutation = useCreateCapaFromTicket();

  const [formData, setFormData] = useState<QmsCreateCapaPayload>({
    title: `CAPA: ${ticket.title}`,
    department: ticket.department,
    priority: ticket.priority || 3,
  });
  const [error, setError] = useState<string | null>(null);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: `CAPA: ${ticket.title}`,
        department: ticket.department,
        priority: ticket.priority || 3,
      });
      setError(null);
    }
  }, [isOpen, ticket.title, ticket.department, ticket.priority]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const capa = await createCapaMutation.mutateAsync({
        ticketId: ticket._id,
        payload: formData,
      });
      onCapaCreated(capa);
      onClose();
    } catch (err: any) {
      setError(err?.message || t('createCapa.error'));
    }
  };

  if (!isOpen) return null;

  // Use portal to render outside parent form (avoids nested form error)
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-atek-blue px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="h-5 w-5 text-white" />
                <h2 className="text-lg font-semibold text-white">
                  {t('createCapa.title')}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-white hover:bg-atek-blue-dark transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                {t('createCapa.description', { ticketId: ticket._id })}
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.fields.title')}
              </label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent"
                placeholder={t('createCapa.titlePlaceholder')}
              />
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.fields.department')}
              </label>
              <select
                value={formData.department || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    department: e.target.value as QmsDepartment,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
              >
                <option value="">{t('form.placeholders.selectDepartment')}</option>
                <option value="product">{t('departmentsFull.product')}</option>
                <option value="sales_marketing">{t('departmentsFull.sales_marketing')}</option>
                <option value="finance">{t('departmentsFull.finance')}</option>
                <option value="it">{t('departmentsFull.it')}</option>
                <option value="hr">{t('departmentsFull.hr')}</option>
                <option value="quality">{t('departmentsFull.quality')}</option>
                <option value="operations">{t('departmentsFull.operations')}</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.fields.priority')}
              </label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: parseInt(e.target.value) as QmsPriority,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
              >
                <option value={1}>{t('priority.1')}</option>
                <option value={2}>{t('priority.2')}</option>
                <option value={3}>{t('priority.3')}</option>
                <option value={4}>{t('priority.4')}</option>
                <option value={5}>{t('priority.5')}</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={createCapaMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-atek-blue text-white rounded-lg hover:bg-atek-blue-dark transition-colors disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {createCapaMutation.isPending
                  ? t('createCapa.creating')
                  : t('createCapa.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
